import {
    Icon,
    IconAlertSquareRounded,
    IconAlertSquareRoundedFilled,
    IconAlertTriangle,
    IconAlertTriangleFilled,
    IconCircleCheck,
    IconCircleCheckFilled,
    IconInfoCircle,
    IconInfoCircleFilled,
} from '@tabler/icons-react'
import { ElementType, ReactNode, Ref, SVGProps } from 'react'

import { MagicIcon } from '../../icons/MagicIcon'
import { AstraButton } from '../Inputs/AstraButton/AstraButton'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../Utilities/astraTwMerge'
import { BodyVariant, SizeOptions } from './../../types/types'

type Variant = 'outlined' | 'filled'
type Color = 'default' | 'info' | 'success' | 'caution' | 'critical' | 'magic'

type Props = {
    /**
    The size of the callout - affects both font and icon sizes
     */
    size?: SizeOptions
    /**
    Outlined should be used for lighter impact, while filled should be used for a stronger emphasis.
    */
    variant?: Variant
    /**
    The color of the callout, depending on the nature or severity of the message displayed.
    */
    color?: Color
    /**
    The text to display in the callout
    */
    text: ReactNode
    /**
     * Optional custom icon to override the default icon for the color
     */
    icon?: Icon | ElementType<SVGProps<SVGSVGElement>>
    /**
     * The optional action button to display in the callout
     * @default undefined
     * */
    action?: {
        label?: string
        icon?: ReactNode
        onClick: () => void
    }
    /**
     * Ref to the div element
     */
    ref?: Ref<HTMLDivElement>
    /**
     * data-testid for testing purposes
     */
    'data-test'?: string
}

const fontStyles: Record<SizeOptions, BodyVariant> = {
    small: 'body-small',
    medium: 'body',
    large: 'body-large',
}

const typeStyles: Record<Color, string> = {
    default: 'bg-surface-strong border-strong',
    info: 'bg-info-subtle-rest border-info-base',
    success: 'bg-success-subtle-rest border-success-base',
    caution: 'bg-caution-subtle-rest border-caution-base',
    critical: 'bg-critical-subtle-rest border-critical-base',
    magic: 'bg-magic-subtle-rest border-magic-subtle',
}

const variantStyles: Record<Variant, string> = {
    outlined: 'bg-surface-base',
    filled: 'border-transparent',
}

const sizeStyles: Record<SizeOptions, string> = {
    small: 'rounded gap-2 pl-2 pr-2.5 py-1.5',
    medium: 'rounded-md gap-2.5 pl-2.5 pr-3 py-2',
    large: 'rounded-lg gap-3 pl-3 pr-3.5 py-2',
}

const iconFilled: Record<Color, Icon | ElementType<SVGProps<SVGSVGElement>>> = {
    default: IconInfoCircleFilled,
    info: IconInfoCircleFilled,
    success: IconCircleCheckFilled,
    caution: IconAlertSquareRoundedFilled,
    critical: IconAlertTriangleFilled,
    magic: MagicIcon,
}

const iconOutlined: Record<Color, Icon | ElementType<SVGProps<SVGSVGElement>>> = {
    default: IconInfoCircle,
    info: IconInfoCircle,
    success: IconCircleCheck,
    caution: IconAlertSquareRounded,
    critical: IconAlertTriangle,
    magic: MagicIcon,
}

const iconSizeStyles: Record<SizeOptions, string> = {
    small: 'size-4 min-w-4',
    medium: 'size-4 min-w-4',
    large: 'size-5 min-w-5',
}

// Nudges the icon down so it aligns with the cap height of the medium body text
const iconWrapperStyles: Partial<Record<SizeOptions, string>> = {
    medium: 'pt-0.5',
}

const textColorStyles: Record<Color, string> = {
    default: 'text-default',
    info: 'text-info',
    success: 'text-success',
    caution: 'text-caution',
    critical: 'text-critical',
    magic: 'text-magic',
}

// Outlined is the lighter-impact variant, so its icon stays on the same status
// token as the text rather than the emphasis token used on a filled background
const iconColorStyles: Record<Variant, Record<Color, string>> = {
    filled: {
        default: 'text-muted',
        info: 'text-info',
        success: 'text-success-emphasis',
        caution: 'text-caution-emphasis',
        critical: 'text-critical-emphasis',
        magic: 'text-magic-emphasis',
    },
    outlined: {
        default: 'text-default',
        info: 'text-info',
        success: 'text-success',
        caution: 'text-caution',
        critical: 'text-critical',
        magic: 'text-magic',
    },
}

export const AstraCallout = ({ ref, size = 'medium', color: type = 'default', variant = 'filled', text, icon: customIcon, action, ...props }: Props) => {
    const Icon = ({ ...props }) => {
        const Comp = customIcon ?? (variant === 'filled' ? iconFilled[type] : iconOutlined[type])
        return <Comp {...props} />
    }

    return (
        <div ref={ref} {...props} className={astraTwMerge('flex border', sizeStyles[size], typeStyles[type], variantStyles[variant])}>
            <div className={iconWrapperStyles[size]}>
                <Icon className={astraTwMerge(iconSizeStyles[size], iconColorStyles[variant][type])} />
            </div>
            {typeof text === 'string' ? (
                <AstraParagraph variant={fontStyles[size]} className={textColorStyles[type]}>
                    {text}
                </AstraParagraph>
            ) : (
                text
            )}
            {action && (
                <AstraButton
                    size="small"
                    variant="secondary"
                    onClick={() => {
                        action.onClick()
                    }}
                    label={action.label}
                    icon={action.icon}
                />
            )}
        </div>
    )
}
